<template>
  <div class="min-h-screen bg-[#F8FAFC] py-6 px-4 lg:px-8">
    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center min-h-[60vh]">
      <div class="animate-spin rounded-full h-12 w-12 border-4 border-[#2563EB] border-t-transparent"></div>
    </div>

    <div v-else-if="ipo" class="max-w-[1400px] mx-auto">
      <Breadcrumbs :items="[{label:'Home',to:'/'},{label:'IPOs',to:'/ipos'},{label:shortName + ' IPO'}]" />
      <div class="flex gap-6">
      <!-- LEFT SIDEBAR -->
      <aside v-if="!underReview" class="w-72 shrink-0 hidden lg:block">
        <div class="sticky top-20 space-y-5">
          <!-- Identity Card -->
          <div class="bg-white border border-gray-200 rounded-2xl p-5">
            <div class="flex items-center gap-3 mb-3">
              <CompanyLogo :name="ipo.name" :logo-url="ipo.company?.logo_url" size="lg" />
              <div>
                <p class="font-semibold text-[#1E293B] text-sm leading-tight">{{ ipo.name }}</p>
                <span :class="statusClass" class="text-xs font-medium px-2 py-0.5 rounded-full capitalize">{{ ipo.status }}</span>
              </div>
            </div>
            <p class="text-xs text-[#64748B]">{{ [ipo.issue_type, ipo.sale_type].filter(Boolean).join(' • ') || '—' }}</p>
            <p v-if="ipo.company?.sector" class="text-xs text-[#64748B] mt-1">{{ ipo.company.sector }}</p>
          </div>

          <!-- Dates -->
          <div class="bg-white border border-gray-200 rounded-2xl p-5">
            <h3 class="font-semibold text-[#1E293B] text-sm mb-3">Key Dates</h3>
            <ul class="space-y-2 text-xs">
              <li v-for="d in datesList" :key="d.label" class="flex justify-between">
                <span class="text-[#64748B]">{{ d.label }}</span>
                <span class="text-[#1E293B] font-medium">{{ d.value }}</span>
              </li>
            </ul>
          </div>

          <!-- Price & Lot -->
          <div class="bg-white border border-gray-200 rounded-2xl p-5 space-y-2 text-sm">
            <div class="flex justify-between"><span class="text-[#64748B]">Price Band</span><span class="font-semibold text-[#1E293B]">{{ priceBand }}</span></div>
            <div class="flex justify-between"><span class="text-[#64748B]">Lot Size</span><span class="font-semibold text-[#1E293B]">{{ ipo.lot_size ? ipo.lot_size + ' shares' : pendingOrDash }}</span></div>
            <div class="flex justify-between"><span class="text-[#64748B]">Face Value</span><span class="font-semibold text-[#1E293B]">{{ rupees(ipo.face_value) }}</span></div>
          </div>

          <!-- Documents -->
          <div v-if="ipo.drhp_url || ipo.rhp_url" class="bg-white border border-gray-200 rounded-2xl p-5 space-y-2">
            <a v-if="ipo.rhp_url" :href="ipo.rhp_url" target="_blank" rel="noopener nofollow" class="block w-full text-center text-xs font-medium py-2 rounded-lg bg-[#16A34A]/10 text-[#16A34A] hover:bg-[#16A34A]/20 transition">RHP Document ↗</a>
            <a v-if="ipo.drhp_url" :href="ipo.drhp_url" target="_blank" rel="noopener nofollow" class="block w-full text-center text-xs font-medium py-2 rounded-lg bg-[#2563EB]/10 text-[#2563EB] hover:bg-[#2563EB]/20 transition">DRHP Document ↗</a>
          </div>

          <!-- Quick Links -->
          <div v-if="sections.length" class="bg-white border border-gray-200 rounded-2xl p-5">
            <h3 class="font-semibold text-[#1E293B] text-sm mb-3">On this page</h3>
            <nav class="space-y-1.5 text-xs">
              <a v-for="s in sections" :key="s.id" :href="'#' + s.id" class="block text-[#64748B] hover:text-[#2563EB] transition">{{ s.heading }}</a>
            </nav>
          </div>

          <!-- Help -->
          <div class="bg-[#2563EB]/5 border border-[#2563EB]/20 rounded-2xl p-5">
            <h3 class="font-semibold text-[#2563EB] text-sm mb-1">Checking allotment?</h3>
            <p class="text-xs text-[#64748B] mb-3">{{ ipo.registrar ? `Allotment is handled by the registrar, ${ipo.registrar}.` : 'Allotment is handled by the issue’s registrar.' }}</p>
            <router-link to="/learn/how-to-check-ipo-allotment-status" class="block w-full text-center text-xs font-medium py-2 rounded-lg bg-[#2563EB] text-white hover:bg-blue-700 transition">How to check allotment →</router-link>
          </div>

          <!-- Broker Affiliate -->
          <BrokerAffiliate />
        </div>
      </aside>

      <!-- MAIN CONTENT -->
      <main class="flex-1 min-w-0 space-y-6">
        <!-- Header -->
        <div class="bg-white border border-gray-200 rounded-2xl p-5 lg:p-6">
          <div class="flex items-center gap-4 mb-3">
            <CompanyLogo :name="ipo.name" :logo-url="ipo.company?.logo_url" size="lg" />
            <div>
              <h1 class="text-xl lg:text-2xl font-bold text-[#1E293B] leading-tight">{{ ipo.name }}</h1>
              <p v-if="!underReview" class="text-sm text-[#64748B] mt-0.5"><span class="capitalize">{{ ipo.status }}</span>{{ headerMeta }}</p>
            </div>
          </div>
          <p v-if="narrative.summary" class="text-[#334155] leading-relaxed">{{ narrative.summary }}</p>
          <DataFreshness :value="narrative.freshness" hide-sources />
        </div>

        <p v-if="underReview" class="text-sm text-[#64748B]"><router-link to="/ipos" class="text-[#2563EB] underline">Browse other IPOs</router-link> · <router-link to="/corrections-policy" class="text-[#2563EB] underline">How we handle corrections</router-link></p>

        <template v-else>
        <!-- Top Stats -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div class="bg-white border border-gray-200 rounded-2xl p-5">
            <p class="text-xs text-[#64748B] mb-1">Price band</p>
            <p class="text-2xl font-bold text-[#1E293B]">{{ priceBand }}</p>
            <p class="text-xs text-[#64748B] mt-1">Min. investment: {{ minInvestment }}</p>
          </div>
          <div class="bg-white border border-gray-200 rounded-2xl p-5">
            <p class="text-xs text-[#64748B] mb-1">Overall subscription</p>
            <p class="text-2xl font-bold text-[#2563EB]">{{ latestSub?.total != null ? times(latestSub.total) : '—' }}</p>
            <p class="text-xs text-[#64748B] mt-1">{{ latestSub?.subscription_date ? 'As of ' + fmt(latestSub.subscription_date) : (ipo.status === 'upcoming' ? 'Available once bidding opens' : 'Not reported') }}</p>
          </div>
          <div class="bg-white border border-gray-200 rounded-2xl p-5">
            <p class="text-xs text-[#64748B] mb-1">Grey market premium <span class="text-[10px]">(unofficial)</span></p>
            <p class="text-2xl font-bold text-[#16A34A]">{{ latestGmp ? rupees(latestGmp.gmp) : '—' }}</p>
            <p class="text-xs text-[#64748B] mt-1">{{ latestGmp ? 'Recorded ' + fmt(latestGmp.recorded_at) + (latestGmp.kostak ? ' · Kostak ' + rupees(latestGmp.kostak) : '') : 'No quote recorded' }}</p>
          </div>
        </div>

        <!-- Narrative sections: company, overview, timeline, financials, subscription, GMP, allotment, documents -->
        <NarrativeSection v-for="s in sections" :key="s.id" :section="s" />

        <section class="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 class="font-semibold text-[#1E293B] text-lg mb-3">Sources and data methodology</h2>
          <p class="text-sm text-[#334155]">{{ (narrative.freshness?.sources || []).join('; ') || '—' }}. <router-link to="/methodology" class="text-[#2563EB] underline">Methodology</router-link> · <router-link to="/corrections-policy" class="text-[#2563EB] underline">Report an error</router-link></p>
          <div v-if="lastCheckedText" class="flex items-center gap-1.5 mt-3 text-xs text-[#64748B]">
            <span>🔍 Last checked {{ lastCheckedText }}</span>
            <span
              class="cursor-help text-[#94A3B8]"
              title="Our system automatically reviews this IPO's data daily for internal consistency and cross-references it against an independent source. This isn't a guarantee the data is fully accurate — it means it was recently and automatically reviewed."
            >ⓘ</span>
          </div>
        </section>

        <section class="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 class="font-semibold text-[#1E293B] text-lg mb-3">Tools</h2>
          <ul class="flex flex-wrap gap-x-5 gap-y-1 text-sm">
            <li><router-link to="/subscriptions" class="text-[#2563EB] hover:underline">Subscription</router-link></li>
            <li><router-link to="/gmp" class="text-[#2563EB] hover:underline">GMP</router-link></li>
            <li><router-link to="/allotments" class="text-[#2563EB] hover:underline">Allotment dates</router-link></li>
            <li><router-link :to="`/compare?ipos=${ipo.slug}`" class="text-[#2563EB] hover:underline">Compare</router-link></li>
          </ul>
        </section>

        <!-- Related IPOs (mirrors ssr/pages/ipo-show.blade.php) -->
        <section v-if="related.length" id="peers" class="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 class="font-semibold text-[#1E293B] text-lg mb-3">IPOs around the same time</h2>
          <ul class="text-sm space-y-1">
            <li v-for="p in related" :key="p.slug"><router-link :to="p.url" class="text-[#2563EB] hover:underline">{{ p.short_name }}</router-link> <span class="text-[#64748B]">· opened {{ p.open_label || '—' }}</span></li>
          </ul>
        </section>

        <section v-if="relatedNews.length" class="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 class="font-semibold text-[#1E293B] text-lg mb-3">In the news</h2>
          <ul class="space-y-1 text-sm">
            <li v-for="a in relatedNews" :key="a.slug"><router-link :to="a.url" class="text-[#2563EB] hover:underline">{{ a.title }}</router-link> <span class="text-[#64748B]">· {{ a.published_label }}</span></li>
          </ul>
        </section>

        <section v-if="guides.length" class="bg-white border border-gray-200 rounded-2xl p-6">
          <h2 class="font-semibold text-[#1E293B] text-lg mb-3">Related guides</h2>
          <ul class="text-sm space-y-1">
            <li v-for="g in guides" :key="g.slug"><router-link :to="g.url" class="text-[#2563EB] hover:underline">{{ g.title }}</router-link></li>
          </ul>
        </section>

        <!-- Mobile-only: affiliate block (sidebar is hidden below lg) -->
        <div class="lg:hidden"><BrokerAffiliate /></div>
        </template>
      </main>
    </div>
    </div>

    <!-- 404 -->
    <div v-else-if="!loading" class="flex flex-col items-center justify-center min-h-[60vh]">
      <p class="text-lg text-[#64748B]">{{ loadError ? 'IPO details could not be loaded. Please try again shortly.' : 'IPO not found.' }}</p>
      <router-link to="/ipos" class="mt-4 text-[#2563EB] hover:underline text-sm">← Back to IPOs</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import Breadcrumbs from '../components/Breadcrumbs.vue';
import BrokerAffiliate from '../components/BrokerAffiliate.vue';
import CompanyLogo from '../components/CompanyLogo.vue';
import NarrativeSection from '../components/NarrativeSection.vue';
import DataFreshness from '../components/DataFreshness.vue';
import { rupees, times } from '../format';

const route = useRoute();
const ipo = ref(null);
const context = ref({});
const loading = ref(true);
const loadError = ref(false);

async function load(slug) {
  loading.value = true;
  loadError.value = false;
  try {
    const [detail, ctx] = await Promise.all([
      axios.get(`/api/ipos/${slug}`),
      axios.get(`/api/ipos/${slug}/context`).catch(() => ({ data: {} })),
    ]);
    ipo.value = detail.data?.data || detail.data;
    context.value = ctx.data || {};
  } catch (e) {
    ipo.value = null;
    loadError.value = e.response?.status !== 404;
  } finally {
    loading.value = false;
  }
}

watch(() => route.params.slug, (slug) => { if (slug) load(slug); }, { immediate: true });

const narrative = computed(() => context.value.narrative || {});
// Rows whose stored details conflict with exchange records show a notice only.
const underReview = computed(() => narrative.value.under_review === true);
const sections = computed(() => narrative.value.sections || []);
const related = computed(() => context.value.related_ipos || []);
const relatedNews = computed(() => context.value.related_news || []);
const guides = computed(() => (context.value.guides || []).filter(Boolean));

const shortName = computed(() => (ipo.value?.name || '').replace(/\s+IPO$/i, ''));
const headerMeta = computed(() => {
  const bits = [ipo.value?.company?.sector, ipo.value?.exchange, ipo.value?.issue_type].filter(Boolean);
  return bits.length ? ' • ' + bits.join(' • ') : '';
});

// A missing field is "to be announced" only while the issue is still ahead of
// us; for closed/listed issues it is simply not available.
const pendingOrDash = computed(() => ['upcoming', 'open'].includes(ipo.value?.status) ? 'To be announced' : '—');
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : pendingOrDash.value;

const priceBand = computed(() => {
  const min = ipo.value?.price_band_min;
  const max = ipo.value?.price_band_max;
  if (min == null && max == null) return pendingOrDash.value;
  if (min == null || max == null || Number(min) === Number(max)) return rupees(min ?? max);
  return `${rupees(min)} – ${rupees(max)}`;
});

const minInvestment = computed(() => {
  if (!ipo.value?.lot_size || !ipo.value?.price_band_max) return '—';
  return rupees(ipo.value.lot_size * ipo.value.price_band_max);
});

const latestGmp = computed(() => {
  const g = (ipo.value?.gmp || []).filter(x => x.gmp != null);
  if (!g.length) return null;
  return [...g].sort((a, b) => String(a.recorded_at).localeCompare(String(b.recorded_at))).at(-1);
});

const latestSub = computed(() => {
  const s = ipo.value?.subscriptions || [];
  if (!s.length) return null;
  return [...s].sort((a, b) => String(a.subscription_date).localeCompare(String(b.subscription_date))).at(-1);
});

const statusClass = computed(() => {
  const map = { open: 'bg-[#16A34A]/10 text-[#16A34A]', upcoming: 'bg-[#2563EB]/10 text-[#2563EB]', closed: 'bg-amber-100 text-amber-700', listed: 'bg-[#7C3AED]/10 text-[#7C3AED]' };
  return map[ipo.value?.status] || 'bg-gray-100 text-gray-600';
});

const datesList = computed(() => {
  if (!ipo.value) return [];
  return [
    { label: 'Open Date', value: fmt(ipo.value.open_date) },
    { label: 'Close Date', value: fmt(ipo.value.close_date) },
    { label: 'Allotment', value: fmt(ipo.value.allotment_date) },
    { label: 'Refund', value: fmt(ipo.value.refund_date) },
    { label: 'Credit', value: fmt(ipo.value.credit_date) },
    { label: 'Listing', value: fmt(ipo.value.listing_date) },
  ];
});

function timeAgo(date) {
  const diff = (Date.now() - new Date(date).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const lastCheckedText = computed(() => {
  const checkedAt = ipo.value?.data_checked_at;
  if (!checkedAt) return null;

  const ageMs = Date.now() - new Date(checkedAt).getTime();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  if (ageMs > sevenDaysMs) return null;

  return timeAgo(checkedAt);
});
</script>
